import {
  NodeType,
  Edge,
  VoicemailGreetingType_Enum,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useQueryClient } from 'react-query';
import { CallRouteV1 } from '@frontend/api-call-route';
import { ExtendedCallRoute } from '../components/call-routes/types';

interface VoicemailExpansionData {
  voicemailBoxName?: string;
  greetingMediaFileName?: string;
  greetingMediaItemId?: string;
  downloadUrl?: string;
  greetingType?: VoicemailGreetingType_Enum;
  sharedAccess?: boolean;
  sendNotification?: boolean;
  emailAddresses?: string[];
  smsNumbers?: string[];
}

type CachedCallRouteData = {
  nodes?: CallRouteV1.Types.Node[];
  callRouteId?: string;
  edges?: Edge[];
  callRoute?: ExtendedCallRoute;
};

export const useVoicemailCacheUpdater = (voicemailBoxId: string) => {
  const queryClient = useQueryClient();

  const updateCallRouteCaches = (expansionData: VoicemailExpansionData) => {
    queryClient.setQueriesData(
      {
        queryKey: ['call-route-v1', 'Read'],
        exact: false,
      },
      (oldCallRoute: CachedCallRouteData | undefined) => {
        if (!oldCallRoute?.nodes) {
          return oldCallRoute || {};
        }

        // Update only nodes in the nodes array that are voicemail boxes with matching ID
        const updatedNodes = oldCallRoute.nodes.map((node) => {
          if (
            node.type === NodeType.NODE_TYPE_VOICEMAIL_BOX &&
            node.callObject?.primitiveId === voicemailBoxId &&
            node.callObject?.voicemailExpansion
          ) {
            return {
              ...node,
              callObject: {
                ...node.callObject,
                ...(expansionData.voicemailBoxName && { primitiveName: expansionData.voicemailBoxName }),
                voicemailExpansion: {
                  ...node.callObject.voicemailExpansion,
                  ...(expansionData.greetingMediaItemId !== undefined && {
                    greetingMediaItemId: expansionData.greetingMediaItemId,
                  }),
                  ...(expansionData.greetingMediaFileName !== undefined && {
                    greetingMediaFileName: expansionData.greetingMediaFileName,
                  }),
                  ...(expansionData.downloadUrl !== undefined && {
                    downloadUrl: expansionData.downloadUrl,
                  }),
                  ...(expansionData.greetingType !== undefined && {
                    greetingType: expansionData.greetingType,
                  }),
                  ...(expansionData.sharedAccess !== undefined && {
                    sharedAccess: expansionData.sharedAccess,
                  }),
                  ...(expansionData.sendNotification !== undefined && {
                    sendNotification: expansionData.sendNotification,
                  }),
                  ...(expansionData.emailAddresses !== undefined && {
                    emailAddresses: expansionData.emailAddresses,
                  }),
                  ...(expansionData.smsNumbers !== undefined && {
                    smsNumbers: expansionData.smsNumbers,
                  }),
                },
              },
            };
          }
          return node;
        });

        return {
          ...oldCallRoute,
          nodes: updatedNodes,
        };
      }
    );
  };

  return {
    updateCallRouteCaches,
  };
};
